import { CSSObject, Theme } from '@mui/material/styles';
import {
  red,
  pink,
  indigo,
  blue,
  teal,
  green,
  lime,
  amber,
  deepOrange,
  grey,
  purple,
} from '@mui/material/colors';

/**
 * Custom color mappings for scheduler events.
 * These are derived from Material UI color palettes but with custom selections
 * for optimal visual appearance in both light and dark modes.
 */
const eventColorTokens = {
  red: {
    light: {
      main: red[500],
      'surface-bold': '#ffa3a3',
      'surface-bold-hover': '#ff9494',
      'on-surface-bold': '#770000',
      'surface-selected': red[600],
      'surface-selected-hover': red[700],
      'on-surface-selected': '#FFFFFF',
      'surface-subtle': red[50],
      'surface-subtle-hover': '#FEDCDF',
      'surface-accent': red[600],
      'on-surface-subtle-primary': '#931717',
      'on-surface-subtle-secondary': red[900],
    },
    dark: {
      main: red[600],
      'surface-bold': '#821b1b',
      'surface-bold-hover': '#A02424',
      'on-surface-bold': '#FFFFFF',
      'surface-selected': red[800],
      'surface-selected-hover': red[900],
      'on-surface-selected': '#FFFFFF',
      'surface-subtle': '#330909',
      'surface-subtle-hover': '#450C0C',
      'surface-accent': red[600],
      'on-surface-subtle-primary': red[100],
      'on-surface-subtle-secondary': red[300],
    },
  },
  pink: {
    light: {
      main: pink[400],
      'surface-bold': '#fca4c1',
      'surface-bold-hover': pink[200],
      'on-surface-bold': '#840f4d',
      'surface-selected': pink[400],
      'surface-selected-hover': '#e9437b',
      'on-surface-selected': '#FFFFFF',
      'surface-subtle': pink[50],
      'surface-subtle-hover': '#FACCDC',
      'surface-accent': pink[400],
      'on-surface-subtle-primary': pink[900],
      'on-surface-subtle-secondary': pink[800],
    },
    dark: {
      main: pink[600],
      'surface-bold': '#67103E',
      'surface-bold-hover': '#73234D',
      'on-surface-bold': '#FFFFFF',
      'surface-selected': pink[800],
      'surface-selected-hover': '#c22066',
      'on-surface-selected': '#FFFFFF',
      'surface-subtle': '#43132d',
      'surface-subtle-hover': '#4F1735',
      'surface-accent': pink[600],
      'on-surface-subtle-primary': pink[50],
      'on-surface-subtle-secondary': pink[200],
    },
  },
  purple: {
    light: {
      main: purple[400],
      'surface-bold': '#D6ABDD',
      'surface-bold-hover': purple[200],
      'on-surface-bold': '#380673',
      'surface-selected': purple[400],
      'surface-selected-hover': '#993ea8',
      'on-surface-selected': '#FFFFFF',
      'surface-subtle': purple[50],
      'surface-subtle-hover': '#EDD0F1',
      'surface-accent': purple[400],
      'on-surface-subtle-primary': '#380673',
      'on-surface-subtle-secondary': purple[700],
    },
    dark: {
      main: purple[700],
      'surface-bold': '#5A0C70',
      'surface-bold-hover': '#751690',
      'on-surface-bold': '#FFFFFF',
      'surface-selected': purple[700],
      'surface-selected-hover': purple[800],
      'on-surface-selected': '#FFFFFF',
      'surface-subtle': '#2c153a',
      'surface-subtle-hover': '#361A47',
      'surface-accent': purple[300],
      'on-surface-subtle-primary': purple[50],
      'on-surface-subtle-secondary': purple[200],
    },
  },
  indigo: {
    light: {
      main: indigo[500],
      'surface-bold': '#bab4fc',
      'surface-bold-hover': '#A59DFB',
      'on-surface-bold': indigo[900],
      'surface-selected': indigo[500],
      'surface-selected-hover': indigo[600],
      'on-surface-selected': '#FFFFFF',
      'surface-subtle': indigo[50],
      'surface-subtle-hover': '#D3D6EE',
      'surface-accent': indigo[500],
      'on-surface-subtle-primary': indigo[900],
      'on-surface-subtle-secondary': indigo[700],
    },
    dark: {
      main: indigo[600],
      'surface-bold': '#19205E',
      'surface-bold-hover': '#2B316B',
      'on-surface-bold': '#FFFFFF',
      'surface-selected': indigo[600],
      'surface-selected-hover': '#4957B2',
      'on-surface-selected': '#FFFFFF',
      'surface-subtle': '#13163c',
      'surface-subtle-hover': '#191d49',
      'surface-accent': indigo[300],
      'on-surface-subtle-primary': indigo[50],
      'on-surface-subtle-secondary': indigo[200],
    },
  },
  blue: {
    light: {
      main: blue[600],
      'surface-bold': blue[200],
      'surface-bold-hover': '#5EB5FC',
      'on-surface-bold': '#0B3A84',
      'surface-selected': blue[700],
      'surface-selected-hover': blue[800],
      'on-surface-selected': '#FFFFFF',
      'surface-subtle': blue[50],
      'surface-subtle-hover': '#CAE5FC',
      'surface-accent': blue[700],
      'on-surface-subtle-primary': '#0B3A84',
      'on-surface-subtle-secondary': blue[800],
    },
    dark: {
      main: blue[600],
      'surface-bold': '#00347c',
      'surface-bold-hover': '#043668',
      'on-surface-bold': '#FFFFFF',
      'surface-selected': blue[700],
      'surface-selected-hover': blue[800],
      'on-surface-selected': '#FFFFFF',
      'surface-subtle': '#002040',
      'surface-subtle-hover': '#032547',
      'surface-accent': blue[800],
      'on-surface-subtle-primary': blue[50],
      'on-surface-subtle-secondary': blue[200],
    },
  },
  teal: {
    light: {
      main: teal[600],
      'surface-bold': teal[200],
      'surface-bold-hover': '#3DBEB2',
      'on-surface-bold': '#00473b',
      'surface-selected': teal[600],
      'surface-selected-hover': teal[700],
      'on-surface-selected': '#FFFFFF',
      'surface-subtle': teal[50],
      'surface-subtle-hover': '#C9E9E6',
      'surface-accent': teal[600],
      'on-surface-subtle-primary': teal[900],
      'on-surface-subtle-secondary': teal[800],
    },
    dark: {
      main: teal[700],
      'surface-bold': '#044036',
      'surface-bold-hover': '#034539',
      'on-surface-bold': '#FFFFFF',
      'surface-selected': teal[800],
      'surface-selected-hover': teal[800],
      'on-surface-selected': '#FFFFFF',
      'surface-subtle': '#023028',
      'surface-subtle-hover': '#03342c',
      'surface-accent': teal[700],
      'on-surface-subtle-primary': teal[50],
      'on-surface-subtle-secondary': teal[200],
    },
  },
  green: {
    light: {
      main: green[600],
      'surface-bold': green[200],
      'surface-bold-hover': '#80CF83',
      'on-surface-bold': '#164B1A',
      'surface-selected': '#2E7D32',
      'surface-selected-hover': '#316F35',
      'on-surface-selected': '#FFFFFF',
      'surface-subtle': green[50],
      'surface-subtle-hover': '#D5ECD6',
      'surface-accent': green[800],
      'on-surface-subtle-primary': '#164B1A',
      'on-surface-subtle-secondary': green[800],
    },
    dark: {
      main: green[700],
      'surface-bold': '#0F3D13',
      'surface-bold-hover': '#133515',
      'on-surface-bold': '#FFFFFF',
      'surface-selected': green[800],
      'surface-selected-hover': green[900],
      'on-surface-selected': '#FFFFFF',
      'surface-subtle': '#0e2810',
      'surface-subtle-hover': '#0D3010',
      'surface-accent': green[300],
      'on-surface-subtle-primary': green[50],
      'on-surface-subtle-secondary': green[200],
    },
  },
  lime: {
    light: {
      main: lime[700],
      'surface-bold': '#D0DF86',
      'surface-bold-hover': '#CAE059',
      'on-surface-bold': '#524b07',
      'surface-selected': '#959B1F',
      'surface-selected-hover': '#898F1D',
      'on-surface-selected': '#FFFFFF',
      'surface-subtle': lime[50],
      'surface-subtle-hover': '#F3F6D0',
      'surface-accent': lime[900],
      'on-surface-subtle-primary': '#625A0D',
      'on-surface-subtle-secondary': lime[900],
    },
    dark: {
      main: lime[800],
      'surface-bold': '#50490A',
      'surface-bold-hover': '#5E571D',
      'on-surface-bold': '#FFFFFF',
      'surface-selected': lime[900],
      'surface-selected-hover': '#706510',
      'on-surface-selected': '#FFFFFF',
      'surface-subtle': '#3c380d',
      'surface-subtle-hover': '#433F0F',
      'surface-accent': lime[300],
      'on-surface-subtle-primary': lime[50],
      'on-surface-subtle-secondary': lime[200],
    },
  },
  amber: {
    light: {
      main: '#FFB840',
      // 'surface-bold': amber[800],
      'surface-bold': amber[200],
      'surface-bold-hover': '#FFD75C',
      'on-surface-bold': '#6c4600',
      'surface-selected': '#e09312',
      'surface-selected-hover': '#E98300',
      'on-surface-selected': '#FFFFFF',
      'surface-subtle': '#FFF2CC',
      'surface-subtle-hover': '#FFECB2',
      'surface-accent': '#e09312',
      'on-surface-subtle-primary': '#6c4600',
      'on-surface-subtle-secondary': '#AA7F00',
    },
    dark: {
      main: '#FFB840',
      'surface-bold': '#8e5c00',
      'surface-bold-hover': '#9C6501',
      'on-surface-bold': '#FFFFFF',
      'surface-selected': amber[700],
      'surface-selected-hover': '#D58600',
      'on-surface-selected': '#FFFFFF',
      'surface-subtle': '#3d2504',
      'surface-subtle-hover': '#4D2E05',
      'surface-accent': amber[400],
      'on-surface-subtle-primary': amber[50],
      'on-surface-subtle-secondary': amber[100],
    },
  },
  orange: {
    light: {
      main: deepOrange[400],
      'surface-bold': '#ffb77c',
      'surface-bold-hover': '#FFA55C',
      'on-surface-bold': '#A73B00',
      'surface-selected': deepOrange[400],
      'on-surface-selected': '#FFFFFF',
      'surface-selected-hover': deepOrange[500],
      'surface-subtle': '#ffe3cc',
      'surface-subtle-hover': '#FFD5B2',
      'surface-accent': deepOrange[400],
      'on-surface-subtle-primary': '#A73B00',
      'on-surface-subtle-secondary': deepOrange[700],
    },
    dark: {
      main: deepOrange[700],
      'surface-bold': '#6D2510',
      'surface-bold-hover': '#7D351F',
      'on-surface-bold': '#FFFFFF',
      'surface-selected': deepOrange[600],
      'surface-selected-hover': '#E85F14',
      'on-surface-selected': '#FFFFFF',
      'surface-subtle': '#4b1a0b',
      'surface-subtle-hover': '#7D351F',
      'surface-accent': deepOrange[600],
      'on-surface-subtle-primary': deepOrange[50],
      'on-surface-subtle-secondary': deepOrange[100],
    },
  },
  grey: {
    light: {
      main: grey[400],
      'surface-bold': grey[400],
      'surface-bold-hover': grey[500],
      'on-surface-bold': grey[900],
      'surface-selected': grey[600],
      'surface-selected-hover': grey[700],
      'on-surface-selected': '#FFFFFF',
      'surface-subtle': grey[100],
      'surface-subtle-hover': grey[200],
      'surface-accent': grey[800],
      'on-surface-subtle-primary': grey[900],
      'on-surface-subtle-secondary': grey[800],
    },
    dark: {
      main: grey[600],
      'surface-bold': grey[800],
      'surface-bold-hover': '#4f4f4f',
      'on-surface-bold': '#FFFFFF',
      'surface-selected': grey[700],
      'surface-selected-hover': grey[800],
      'on-surface-selected': '#FFFFFF',
      'surface-subtle': '#1d1d1d',
      'surface-subtle-hover': grey[900],
      'surface-accent': grey[400],
      'on-surface-subtle-primary': grey[50],
      'on-surface-subtle-secondary': grey[400],
    },
  },
} as const;

export type PaletteName = keyof typeof eventColorTokens;

/**
 * Generates palette variants for MUI styled components.
 * Use with styled components: `variants: getPaletteVariants(theme)`
 */
/**
 * Returns the focus-visible background style for grid cells (day cells, time columns, month cells).
 */
export const getCellFocusBackground = (theme: Theme) =>
  theme.alpha((theme.vars || theme).palette.primary.light, 0.12);

export const getPaletteVariants = (theme: Theme) => {
  return Object.entries(eventColorTokens).map(([colorName, colorValues]) => ({
    props: { 'data-palette': colorName as PaletteName },
    style: {
      '--event-main': colorValues.light.main,
      '--event-surface-bold': colorValues.light['surface-bold'],
      '--event-on-surface-bold': colorValues.light['on-surface-bold'],
      '--event-surface-bold-hover':
        colorValues.light['surface-bold-hover'] ||
        'color-mix(in srgb, var(--event-surface-bold), black 4%);',
      '--event-surface-subtle': colorValues.light['surface-subtle'],
      '--event-surface-subtle-hover': colorValues.light['surface-subtle-hover'],
      '--event-surface-accent': colorValues.light['surface-accent'],
      '--event-on-surface-subtle-primary': colorValues.light['on-surface-subtle-primary'],
      '--event-on-surface-subtle-secondary': colorValues.light['on-surface-subtle-secondary'],
      '--event-surface-selected': colorValues.light['surface-selected'],
      '--event-surface-selected-hover': colorValues.light['surface-selected-hover'],
      '--event-on-surface-selected': colorValues.light['on-surface-selected'],
      ...theme.applyStyles('dark', {
        '--event-main': colorValues.dark.main,
        '--event-surface-bold': colorValues.dark['surface-bold'],
        '--event-on-surface-bold': colorValues.dark['on-surface-bold'],
        '--event-surface-bold-hover': colorValues.dark['surface-bold-hover'],
        '--event-surface-subtle': colorValues.dark['surface-subtle'],
        '--event-surface-subtle-hover': colorValues.dark['surface-subtle-hover'],
        '--event-surface-accent': colorValues.dark['surface-accent'],
        '--event-on-surface-subtle-primary': colorValues.dark['on-surface-subtle-primary'],
        '--event-on-surface-subtle-secondary': colorValues.dark['on-surface-subtle-secondary'],
        '--event-surface-selected': colorValues.dark['surface-selected'],
        '--event-surface-selected-hover': colorValues.dark['surface-selected-hover'],
        '--event-on-surface-selected': colorValues.dark['on-surface-selected'],
      }),
    } as CSSObject,
  }));
};
